import * as userQuestionnaireApi from "./userQuestionnaireApi";
import { useEffect, useRef, useState } from "react";
import { getDefaultLocale } from "localization/utils";
import { useNavigate } from "react-router-dom";
import { getUser } from "auth/helpers/authApi";
import { LoadingState } from "types";
import { LinearProgress } from "@mui/material";
import { useAuthContext } from "context/GameAuthContext";
import { isFeatureEnabled } from "feature-configs/featureConfigUtils";
import { FeatureConfig } from "feature-configs/featureConfigApi";

export enum MessageEvents {
	INIT = "INIT",
	SET_GAME_STATE = "SET_GAME_STATE",
	SET_LOCALE = "SET_LOCALE",
	INIT_COMPLETE = "INIT_COMPLETE",

	UPDATE_GAME_STATE = "UPDATE_GAME_STATE",

	SESSION_COMPLETED = "SESSION_COMPLETED",
	EXIT = "EXIT",
}

function postMessage(contentWindow: Window, eventName: string, data?: any) {
	contentWindow.postMessage({ event: eventName, data }, "*");
}

export default function UserQuestionnaire() {
	const navigate = useNavigate();
	const { user } = useAuthContext();
	const [loadingState, setLoadingState] = useState(LoadingState.INIT);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	useEffect(() => {
		async function getUserQuestionnaire() {
			setLoadingState(LoadingState.IN_PROGRESS);
			const response = await userQuestionnaireApi.getUserQuestionnaire();

			if (!response.data) {
				navigate("/user-dashboard", { replace: true });
				return;
			}

			const { isCompleted, isEligible } = response.data;

			// If the user has already completed the questionnaire or is not eligible, redirect to the user dashboard
			if (isCompleted || !isEligible) {
				userQuestionnaireApi.setIsFillingQuestionnaire(false);
				if (user?.isBuddy) {
					navigate("/user-dashboard/buddy");
				} else {
					navigate("/user-dashboard", { replace: true });
				}
				return;
			}
			setLoadingState(LoadingState.DONE);
		}

		// Only show the user questionnaire if the feature is enabled
		if (isFeatureEnabled(FeatureConfig.buddy)) {
			getUserQuestionnaire();
		} else {
			navigate("/user-dashboard", { replace: true });
		}
	}, [navigate, user?.isBuddy]);

	useEffect(() => {
		async function getUserQuestionnaire() {
			setLoadingState(LoadingState.IN_PROGRESS);
			const response = await userQuestionnaireApi.getUserQuestionnaire();

			if (!response.data) {
				setLoadingState(LoadingState.DONE);
				navigate("/user-dashboard", { replace: true });
				return;
			}

			const { questionnaire, isCompleted, isEligible } = response.data;

			// If the user has already completed the questionnaire or is not eligible, redirect to the user dashboard
			if (isCompleted || !isEligible) {
				setLoadingState(LoadingState.DONE);
				userQuestionnaireApi.setIsFillingQuestionnaire(false);
				navigate("/user-dashboard", { replace: true });
				return;
			}
			const contentWindow = iframeRef.current!.contentWindow!;

			// Send the game state to the iframe
			if (questionnaire) {
				const gameState = questionnaire.gameState;
				postMessage(contentWindow, MessageEvents.SET_GAME_STATE, gameState);
			}

			// Send the locale to the iframe
			postMessage(contentWindow, MessageEvents.SET_LOCALE, { locale: getDefaultLocale() });
			// Send the init complete event to the iframe
			postMessage(contentWindow, MessageEvents.INIT_COMPLETE);
			// Set the isFillingQuestionnaire to true. This is used to prevent the user from navigating away from the page
			userQuestionnaireApi.setIsFillingQuestionnaire(true);
			setLoadingState(LoadingState.DONE);
		}

		async function completeQuestionnaire() {
			// Complete the questionnaire
			await userQuestionnaireApi.completeQuestionnaire();
			// Set the isFillingQuestionnaire to false
			userQuestionnaireApi.setIsFillingQuestionnaire(false);
		}

		function onMessage(evt: MessageEvent) {
			console.log("Received event", evt.data.event);
			if (evt.source !== iframeRef.current?.contentWindow) {
				return;
			}

			const { data } = evt;

			if (data.event === MessageEvents.INIT) {
				getUserQuestionnaire();
				return;
			}

			if (data.event === MessageEvents.UPDATE_GAME_STATE) {
				// Update the game state
				userQuestionnaireApi.upsertUserQuestionnaire(data.data);
				return;
			}

			if (data.event === MessageEvents.SESSION_COMPLETED) {
				// Do something when the session is completed
				completeQuestionnaire();
				return;
			}

			if (data.event === MessageEvents.EXIT) {
				// Navigate to the user dashboard
				const user = getUser();
				if (user.isBuddy) {
					// Navigate to buddy page
					navigate("/user-dashboard/buddy", { replace: true });
				} else {
					navigate("/user-dashboard", { replace: true });
				}
			}
		}

		window.addEventListener("message", onMessage);
		return () => {
			window.removeEventListener("message", onMessage);
		};
	}, [navigate]);

	return (
		<div style={{ overflow: "hidden", height: "100%", width: "100%" }}>
			{loadingState === LoadingState.IN_PROGRESS && (
				<div>
					<LinearProgress />
				</div>
			)}
			<iframe
				title="User Questionnaire"
				ref={iframeRef}
				// TODO: Use env variable
				src={process.env.NODE_ENV === "development" ? "http://localhost:5173/island-3d/" : "/island-3d/"}
				width="100%"
				height="100%"
				style={{ pointerEvents: "auto" }}
			></iframe>
		</div>
	);
}
